import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useInteractions,
  hide,
  inline,
} from '@floating-ui/react'
import { useCallback, useRef, useEffect, useState } from 'react'

interface SelectionState {
  startOffset: number
  endOffset: number
  text: string
  rect: DOMRect
}

const getCharacterOffset = (
  container: Node,
  node: Node,
  offset: number
): number => {
  let charOffset = 0
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  )

  let currentNode: Node | null
  while ((currentNode = walker.nextNode())) {
    if (currentNode === node) {
      return charOffset + offset
    }
    charOffset += currentNode.textContent?.length || 0
  }

  return charOffset
}

const createRangeFromOffsets = (
  container: Node,
  startOffset: number,
  endOffset: number
): Range | null => {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  )

  const range = document.createRange()
  let charCount = 0
  let startSet = false
  let endSet = false

  let currentNode: Node | null
  while ((currentNode = walker.nextNode())) {
    const nodeLength = currentNode.textContent?.length || 0

    if (!startSet && charCount + nodeLength >= startOffset) {
      range.setStart(currentNode, startOffset - charCount)
      startSet = true
    }

    if (startSet && !endSet && charCount + nodeLength >= endOffset) {
      range.setEnd(currentNode, endOffset - charCount)
      endSet = true
      break
    }

    charCount += nodeLength
  }

  return startSet && endSet ? range : null
}

const hasExistingTag = (
  range: Range,
  tag: string,
  editor: HTMLElement
): boolean => {
  const selectedContent = range.cloneContents()
  const tempDiv = document.createElement('div')
  tempDiv.appendChild(selectedContent)
  const hasTag = tempDiv.querySelector(tag) !== null

  let currentNode: Node | null = range.commonAncestorContainer
  let isWithinTag = false

  while (currentNode && currentNode !== editor) {
    if (
      currentNode.nodeType === Node.ELEMENT_NODE &&
      (currentNode as Element).tagName.toLowerCase() === tag.toLowerCase()
    ) {
      isWithinTag = true
      break
    }
    currentNode = currentNode.parentNode
  }

  return hasTag || isWithinTag
}

const removeFormatting = (
  range: Range,
  tag: string,
  editor: HTMLElement
): void => {
  const allElements = editor.querySelectorAll(tag)
  allElements.forEach((element) => {
    if (range.intersectsNode(element)) {
      const parent = element.parentNode
      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element)
        }
        parent.removeChild(element)
      }
    }
  })
  editor.normalize()
}

const addFormatting = (
  range: Range,
  tag: string,
  text: string,
  editor: HTMLElement
): void => {
  const element = document.createElement(tag)
  const contents = range.extractContents()

  if (!contents.textContent || contents.textContent.length === 0) {
    element.textContent = text
    range.deleteContents()
    range.insertNode(element)
  } else {
    element.appendChild(contents)
    range.insertNode(element)
  }

  editor.normalize()
}

const restoreSelectionAfterFormatting = (
  editor: HTMLElement,
  startOffset: number,
  endOffset: number,
  selectionRef: React.MutableRefObject<SelectionState | null>,
  refs: ReturnType<typeof useFloating>['refs']
): void => {
  requestAnimationFrame(() => {
    const newRange = createRangeFromOffsets(editor, startOffset, endOffset)

    if (newRange) {
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(newRange)

        const rect = newRange.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          selectionRef.current = {
            startOffset,
            endOffset,
            text: selection.toString(),
            rect,
          }

          refs.setReference({
            getBoundingClientRect: () => newRange.getBoundingClientRect(),
            getClientRects: () => newRange.getClientRects(),
          })
        }
      }
    }
  })
}

interface UseRichTextFormattingOptions {
  editorRef: React.RefObject<HTMLDivElement | null>
}

export const useRichTextFormatting = ({
  editorRef,
}: UseRichTextFormattingOptions) => {
  const selectionRef = useRef<SelectionState | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [inline(), flip(), shift({ padding: 8 }), offset(10), hide()],
    whileElementsMounted: autoUpdate,
  })

  // Handle dismissal when clicking outside
  const dismiss = useDismiss(context)

  const { getFloatingProps } = useInteractions([dismiss])

  // Handle text selection
  useEffect(() => {
    function handleMouseUp(event: MouseEvent) {
      // Don't close if clicking inside the toolbar
      if (refs.floating.current?.contains(event.target as Element | null)) {
        return
      }

      // Small delay to ensure selection is finalized
      setTimeout(() => {
        if (!editorRef.current) return

        const selection = window.getSelection()
        const range =
          typeof selection?.rangeCount === 'number' && selection.rangeCount > 0
            ? selection.getRangeAt(0)
            : null

        // Close if no selection
        if (selection?.isCollapsed) {
          setIsOpen(false)
          return
        }

        // Check if selection is within our editor
        if (
          range &&
          selection &&
          !selection.isCollapsed &&
          editorRef.current.contains(range.commonAncestorContainer)
        ) {
          const text = selection.toString().trim()
          if (text && text.length > 0) {
            // Calculate character offsets
            const startOffset = getCharacterOffset(
              editorRef.current,
              range.startContainer,
              range.startOffset
            )
            const endOffset = getCharacterOffset(
              editorRef.current,
              range.endContainer,
              range.endOffset
            )

            selectionRef.current = {
              startOffset,
              endOffset,
              text,
              rect: range.getBoundingClientRect(),
            }

            // Set virtual reference element for floating-ui
            refs.setReference({
              getBoundingClientRect: () => range.getBoundingClientRect(),
              getClientRects: () => range.getClientRects(),
            })

            setIsOpen(true)
          }
        }
      })
    }

    function handleMouseDown(event: MouseEvent) {
      // Don't close if clicking inside the toolbar
      if (refs.floating.current?.contains(event.target as Element | null)) {
        return
      }

      // Close if selection is collapsed
      if (window.getSelection()?.isCollapsed) {
        setIsOpen(false)
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [editorRef, refs])

  const applyFormatting = useCallback(
    (tag: string) => {
      if (!editorRef.current || !selectionRef.current) return

      const editor = editorRef.current
      const { startOffset, endOffset, text } = selectionRef.current

      Promise.resolve().then(() => {
        editor.focus()

        const range = createRangeFromOffsets(editor, startOffset, endOffset)
        if (!range) return

        const windowSelection = window.getSelection()
        if (!windowSelection) return

        windowSelection.removeAllRanges()
        windowSelection.addRange(range)

        if (hasExistingTag(range, tag, editor)) {
          removeFormatting(range, tag, editor)
        } else {
          addFormatting(range, tag, text, editor)
        }

        const event = new Event('input', { bubbles: true, cancelable: true })
        editor.dispatchEvent(event)

        restoreSelectionAfterFormatting(
          editor,
          startOffset,
          endOffset,
          selectionRef,
          refs
        )
      })
    },
    [refs, editorRef]
  )

  const applyLink = useCallback(
    (url: string) => {
      if (!editorRef.current || !selectionRef.current) return

      const editor = editorRef.current
      const { startOffset, endOffset, text } = selectionRef.current

      editor.focus()

      const range = createRangeFromOffsets(editor, startOffset, endOffset)
      if (!range) return

      const windowSelection = window.getSelection()
      if (windowSelection) {
        windowSelection.removeAllRanges()
        windowSelection.addRange(range)
      }

      const linkElement = document.createElement('a')
      linkElement.href = url.trim()

      try {
        const contents = range.extractContents()
        linkElement.appendChild(contents)
        range.insertNode(linkElement)
      } catch (e) {
        linkElement.textContent = text
        range.deleteContents()
        range.insertNode(linkElement)
      }

      restoreSelectionAfterFormatting(
        editor,
        startOffset,
        endOffset,
        selectionRef,
        refs
      )
    },
    [editorRef, refs]
  )

  return {
    applyFormatting,
    applyLink,
    // Floating-ui props for the toolbar
    isOpen,
    floatingStyles,
    refs,
    getFloatingProps,
    context,
  }
}
